import React from 'react';
import { Link, useLocation, Outlet, useNavigate } from 'react-router-dom';
import { 
  House, 
  Package, 
  Tag, 
  Receipt, 
  Users, 
  EnvelopeSimple, 
  SignOut,
  List,
  CurrencyCircleDollar,
  UserCircle,
  Ticket,
  Star,
  X,
  Images
} from '@phosphor-icons/react';
import { useAuthStore } from '../../store';
import { toast } from 'sonner';
import api from '../../utils/api';

// Check if running on admin subdomain
const isAdminSubdomain = () => {
  const hostname = window.location.hostname;
  return hostname.startsWith('admin.') || hostname.startsWith('admin-');
};

const AdminLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  
  // Prevent body scroll when sidebar is open on mobile
  React.useEffect(() => {
    if (sidebarOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [sidebarOpen]);
  
  // Use different paths based on subdomain vs path routing
  const isSubdomain = isAdminSubdomain();
  
  const navItems = [
    { path: isSubdomain ? '/dashboard' : '/admin/dashboard', icon: House, label: 'Dashboard', exact: true },
    { path: isSubdomain ? '/items' : '/admin/items', icon: Package, label: 'Items' },
    { path: isSubdomain ? '/categories' : '/admin/categories', icon: Tag, label: 'Categories' },
    { path: isSubdomain ? '/reviews' : '/admin/reviews', icon: Star, label: 'Reviews' },
    { path: isSubdomain ? '/invoices' : '/admin/invoices', icon: Receipt, label: 'Invoices' },
    { path: isSubdomain ? '/payments' : '/admin/payments', icon: CurrencyCircleDollar, label: 'Payments' },
    { path: isSubdomain ? '/customers' : '/admin/customers', icon: Users, label: 'Customers' },
    { path: isSubdomain ? '/coupons' : '/admin/coupons', icon: Ticket, label: 'Coupons' },
    { path: isSubdomain ? '/contacts' : '/admin/contacts', icon: EnvelopeSimple, label: 'Contacts' },
    { path: isSubdomain ? '/carousel' : '/admin/carousel', icon: Images, label: 'Carousel' },
    { path: isSubdomain ? '/users' : '/admin/users', icon: UserCircle, label: 'Users' },
  ];
  
  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path || location.pathname === '/admin';
    return location.pathname.startsWith(path);
  };
  
  const handleLogout = async () => {
    try {
      await api.post('/api/auth/logout');
    } catch (error) {
      // Continue with logout even if API call fails
      console.error('Logout API error:', error);
    }
    logout();
    toast.success('Logged out successfully');
    navigate(isAdminSubdomain() ? '/login' : '/admin/login');
  };

  return (
    <div className="min-h-screen bg-background-subtle flex relative">
      {/* Sidebar */}
      <aside className={`admin-sidebar flex flex-col ${sidebarOpen ? 'sidebar-open' : 'sidebar-closed'}`}>
        {/* Logo & Close Button */}
        <div className="p-4 border-b border-white/10 flex items-center justify-between shrink-0">
          <Link to="/" className="flex items-center gap-3">
            <img 
              src="/logo.png" 
              alt="Luster Adorn" 
              className="h-8 w-auto brightness-0 invert"
            />
            <div className="hidden md:block">
              <p className="font-display text-sm text-white">Luster Adorn</p>
              <p className="font-body text-[10px] text-gray-500 uppercase tracking-wider">Admin Panel</p>
            </div>
          </Link>
          {/* Mobile close button */}
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-all"
          >
            <X size={20} />
          </button>
        </div>
        
        {/* Navigation */}
        <nav className="flex-1 py-4 overflow-y-auto scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent">
          {navItems.map((item) => (
            <button
              key={item.path}
              onClick={() => {
                navigate(item.path);
                setSidebarOpen(false);
              }}
              data-testid={`admin-nav-${item.label.toLowerCase()}`}
              className={`admin-nav-item group ${isActive(item.path, item.exact) ? 'active' : ''}`}
            >
              <div className={`nav-icon-wrapper ${isActive(item.path, item.exact) ? 'active' : ''}`}>
                <item.icon size={20} weight={isActive(item.path, item.exact) ? 'fill' : 'regular'} />
              </div>
              <span className="font-body text-sm">{item.label}</span>
              {isActive(item.path, item.exact) && (
                <div className="active-indicator" />
              )}
            </button>
          ))}
        </nav>
        
        {/* User Info & Logout - Always at bottom */}
        <div className="p-4 border-t border-white/10 bg-white/5 shrink-0">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary/30 to-primary/10 flex items-center justify-center border border-primary/20">
              <span className="font-display text-primary text-lg font-medium">
                {user?.username?.charAt(0).toUpperCase() || 'A'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-body text-sm text-white truncate">{user?.username || 'Admin'}</p>
              <p className="font-body text-xs text-gray-500 capitalize">{user?.role || 'admin'}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="admin-logout-btn"
            className="admin-logout-btn w-full"
          >
            <SignOut size={18} />
            <span>Logout</span>
          </button>
        </div>
      </aside>
      
      {/* Main Content */}
      <div className="flex-1 lg:ml-64">
        {/* Top Bar */}
        <header className="bg-white/80 backdrop-blur-md border-b border-border-default/50 px-6 py-4 sticky top-0 z-40">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              data-testid="admin-menu-toggle"
              className="lg:hidden p-2.5 text-secondary hover:text-primary hover:bg-primary/5 rounded-xl transition-all"
            >
              <List size={22} />
            </button>
            <div className="lg:block hidden">
              <h2 className="font-display text-xl text-secondary">
                {navItems.find(item => isActive(item.path, item.exact))?.label || 'Dashboard'}
              </h2>
            </div>
            <Link 
              to="/" 
              target="_blank"
              className="font-body text-sm text-text-secondary hover:text-primary transition-colors flex items-center gap-1"
            >
              View Store <span className="text-xs">↗</span>
            </Link>
          </div>
        </header>
        
        {/* Page Content */}
        <main className="p-6 min-h-[calc(100vh-73px)]">
          <Outlet />
        </main>
      </div>
      
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden animate-fade-in"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default AdminLayout;
